import type { SxProps } from '@mui/material';
import type {
  ColumnDef,
  DataTableColumnMeta,
} from '@ovComponents/2-component/dataTable';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getPortfolioName } from '../utils';
import { getPhantomColumns } from '../utils/phantomColumns';
import { isSortablePortfolioColumn } from '../utils/portfolioSorting';
import {
  formatAdvisorLabel,
  formatCustodianLabel,
  formatModelNameLabel,
  formatReasonLabel,
} from '../utils/tableFormatters';
import type { EnrichedPortfolioReport } from './usePortfolioTableState.types';

export const PORTFOLIO_SELECT_COLUMN_WIDTH = 36;

export const PORTFOLIO_COLUMN_WIDTH = {
  portfolio: 110,
  modelName: 110,
  reason: 66,
  custodian: 66,
  advisor: 66,
  value: 94,
  cashAndPosition: 118,
  lastRebalDate: 92,
  trades: 56,
} as const;

export type PortfolioColumnWidthKey = keyof typeof PORTFOLIO_COLUMN_WIDTH;

const HEADER_LABEL_ELLIPSIS_SX = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

export function portfolioColumnMeta(
  column: PortfolioColumnWidthKey,
  extra?: Partial<DataTableColumnMeta>,
): {
  dataTable: DataTableColumnMeta;
} {
  const px = PORTFOLIO_COLUMN_WIDTH[column];
  return {
    dataTable: {
      width: px,
      minWidth: px,
      maxWidth: px,
      headerLabelSx: HEADER_LABEL_ELLIPSIS_SX,
      ...extra,
    },
  };
}

export function portfolioFixedColumnSx(
  column: PortfolioColumnWidthKey,
): SxProps {
  const px = PORTFOLIO_COLUMN_WIDTH[column];
  return {
    width: px,
    minWidth: px,
    maxWidth: px,
    overflow: 'hidden',
  };
}

export function usePortfolioColumns({
  canReadRepCode,
}: {
  canReadRepCode: boolean;
}): ColumnDef<EnrichedPortfolioReport, unknown>[] {
  const { t } = useTranslation('portfolioTradingHub');

  return useMemo(
    (): ColumnDef<EnrichedPortfolioReport, unknown>[] => [
      ...getPhantomColumns<EnrichedPortfolioReport>(),
      {
        id: 'portfolio',
        header: t('portfoliosTable.columns.portfolio'),
        accessorFn: (row) => getPortfolioName(row),
        enableSorting: false,
        meta: portfolioColumnMeta('portfolio'),
      },
      {
        id: 'modelName',
        header: t('portfoliosTable.columns.modelName'),
        accessorFn: (row) => formatModelNameLabel(row),
        enableSorting: false,
        meta: portfolioColumnMeta('modelName'),
      },
      {
        id: 'reason',
        header: t('portfoliosTable.columns.reason'),
        accessorFn: (row) => formatReasonLabel(row),
        enableSorting: false,
        meta: portfolioColumnMeta('reason'),
      },
      {
        id: 'custodian',
        header: t('portfoliosTable.columns.custodian'),
        accessorFn: (row) => formatCustodianLabel(row),
        enableSorting: false,
        meta: portfolioColumnMeta('custodian'),
      },
      {
        id: 'advisor',
        header: t('portfoliosTable.columns.advisor'),
        accessorFn: (row) => formatAdvisorLabel(row, { canReadRepCode }),
        enableSorting: false,
        meta: portfolioColumnMeta('advisor'),
      },
      {
        id: 'value',
        header: t('portfoliosTable.columns.value'),
        accessorFn: (row) => row.marketValueCents || 0,
        enableSorting: isSortablePortfolioColumn('value'),
        meta: portfolioColumnMeta('value'),
      },
      {
        id: 'cashAndPosition',
        header: t('portfoliosTable.columns.cashAndPosition'),
        accessorFn: (row) => row._drift ?? 0,
        enableSorting: false,
        meta: portfolioColumnMeta('cashAndPosition'),
      },
      {
        id: 'lastRebalDate',
        header: t('portfoliosTable.columns.lastRebalDate'),
        accessorFn: (row) => row.lastOptimizedBy?.createdAt ?? '',
        enableSorting: isSortablePortfolioColumn('lastRebalDate'),
        meta: portfolioColumnMeta('lastRebalDate'),
      },
      {
        id: 'trades',
        header: t('portfoliosTable.columns.trades'),
        accessorFn: (row) => row.pendingSubTradeRequests || 0,
        enableSorting: isSortablePortfolioColumn('trades'),
        meta: portfolioColumnMeta('trades', { align: 'center' }),
      },
    ],
    [t, canReadRepCode],
  );
}
